import { z } from 'zod';

import {
  messageResponseSchema,
  messageSchema,
  messageThreadDetailResponseSchema,
  messageThreadListResponseSchema,
  messageThreadSchema,
  type CreateMessageThreadRequest,
  type Message,
  type MessageThread,
  type MessageThreadDetailResponse,
  type MessageThreadKind,
  type MessageThreadStatus,
  type PatientPortalReplyRequest,
} from '@emr/shared';

import { apiRequest } from '../client.ts';

/**
 * The patient portal's half of secure messaging (`BACKLOG.md` W2 pillar 3, `server/src/
 * patientMessaging/routes.ts`). A separate module from `web/src/api/messaging.ts`, the staff-side
 * client — that file's own header already notes it as the staff half only, with "no web client
 * anywhere in this app yet" for this surface; this is that client, built against the identical
 * `@emr/shared` schemas so the two never drift on wire shape even though they call different paths
 * under a different session.
 *
 * Every path here is static, matching the server route file's own header: `threadId` always travels
 * as a query parameter or body field, never a `:threadId` path segment.
 */

const createThreadResponseSchema = z.strictObject({
  thread: messageThreadSchema,
  message: messageSchema,
});

export interface ListMyThreadsQuery {
  readonly kind?: MessageThreadKind;
  readonly status?: MessageThreadStatus;
}

/** `GET /api/patient-portal/messaging/threads` — every thread belonging to the caller. */
export async function listMyThreads(
  query: ListMyThreadsQuery,
  signal?: AbortSignal,
): Promise<readonly MessageThread[]> {
  const params = new URLSearchParams();
  if (query.kind !== undefined) params.set('kind', query.kind);
  if (query.status !== undefined) params.set('status', query.status);
  const qs = params.toString();
  const response = await apiRequest(
    qs === ''
      ? '/api/patient-portal/messaging/threads'
      : `/api/patient-portal/messaging/threads?${qs}`,
    messageThreadListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.threads;
}

/** `POST /api/patient-portal/messaging/threads` — start a new thread with its first message. */
export async function startMyThread(
  input: CreateMessageThreadRequest,
): Promise<{ readonly thread: MessageThread; readonly message: Message }> {
  return apiRequest('/api/patient-portal/messaging/threads', createThreadResponseSchema, {
    method: 'POST',
    body: input,
  });
}

/** `GET /api/patient-portal/messaging/threads/detail?threadId=...` — one thread plus every
 * message, oldest first. Marks the thread read for the patient as a server-side side effect. */
export async function getMyThreadDetail(
  threadId: string,
  signal?: AbortSignal,
): Promise<MessageThreadDetailResponse> {
  const params = new URLSearchParams({ threadId });
  return apiRequest(
    `/api/patient-portal/messaging/threads/detail?${params.toString()}`,
    messageThreadDetailResponseSchema,
    signal === undefined ? {} : { signal },
  );
}

/** `POST /api/patient-portal/messaging/threads/reply` — reply into an existing thread of the
 * caller's own. */
export async function replyToMyThread(input: PatientPortalReplyRequest): Promise<Message> {
  const response = await apiRequest(
    '/api/patient-portal/messaging/threads/reply',
    messageResponseSchema,
    { method: 'POST', body: input },
  );
  return response.message;
}
